import { hasPermission } from "@avela/avela-authorization-sdk";
import { Box, Button, Flex } from "@chakra-ui/react";
import { Formik } from "formik";
import _, { isEqual } from "lodash";
import React, { useState } from "react";
import {
  GET_CUSTOM_QUESTION_ANSWER,
  GET_SELECTED_PERSON_ANSWER_BANK,
} from "src/components/Inputs/QuestionDynamicInputs/CustomQuestion/modules/api/graphql/queries";
import {
  FORM_AUTHORIZATION_SCOPE,
  useAuthorizationScoped,
} from "src/components/Providers/AuthorizationProvider";
import {
  FormWithAnswersTracker,
  useFormContext,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import { CQT_NO_GUARDIAN_DISABLED_REASON } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useFormAnswerUpsert } from "src/hooks/useFormAnswerUpsert";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { FormQuestion } from "src/scenes/parent/forms/components/Inputs/FormQuestion";
import { DELETE_ANSWERS } from "src/scenes/parent/forms/graphql/mutations";
import { GET_FORM_ANSWERS_BY_ID } from "src/scenes/parent/forms/graphql/queries";
import { useRemoveAnswersForHiddenQuestions } from "src/scenes/parent/forms/hooks/useRemoveAnswersForHiddenQuestions";
import { Answer } from "src/services/formTemplate";
import {
  getAddressAnswersDeletes,
  getAddressAnswersInserts,
  getCustomQuestionAnswerUpsertPayload,
  getDateAnswersInserts,
  getFreeTextAnswersInserts,
  getGradesAnswersInserts,
  getMultiSelectAnswersInserts,
  getNumberAnswersInserts,
  getSingleSelectAnswersInserts,
} from "src/services/formTemplate/answer";
import { RankedSchool } from "src/services/formTemplate/preRankingSection";
import * as Question from "src/services/formTemplate/question";
import * as AFF from "src/services/formTemplateFilters";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import {
  GET_FORM_VIEW_BY_ID,
  GET_SCHOOLS_RANK_VIEW,
} from "../../graphql/queries";
import { hasInvalidAddressCoordinates } from "./hasInvalidAddressCoordinates";
import { Undo } from "./Undo";

type Props = {
  formId: uuid;
  guardianId: uuid | undefined;
  applicant: AFF.Types.Applicant;
  questions: AF.Question<AF.WithId>[];
  form_answers: GQL.FormAnswerFragment[];
  grades_answers: GQL.GradesAnswersFragment[];
  address_answers: GQL.AddressAnswersFragment[];
  custom_question_answers: GQL.CustomQuestionAnswersFragment[];
  custom_question_answer_bank_relationships?: GQL.CustomQuestionAnswerBankRelationshipsFragment[];
  customQuestionTypes: GQL.GetCustomQuestionTypesByOrg_custom_question_type[];
  rankedSchools: RankedSchool[];
  onCloseEdit: () => void;
  onRefetchFormAlerts?: () => Promise<void>;
  previousFormSchoolIds: uuid[];
};

export const GeneralSectionEditForm: React.FC<Props> = ({
  formId,
  guardianId,
  applicant,
  questions,
  form_answers,
  grades_answers,
  address_answers,
  custom_question_answers,
  custom_question_answer_bank_relationships,
  customQuestionTypes,
  rankedSchools,
  onCloseEdit,
  onRefetchFormAlerts,
  previousFormSchoolIds,
}) => {
  const hasAdminOrgAdminPermission = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.DISTRICT_ADMIN,
  ]);

  const authorizationMap = useAuthorizationScoped(FORM_AUTHORIZATION_SCOPE);

  const { upsertFormAnswers, upsertFormAnswersStatus } = useFormAnswerUpsert();
  const [deleteAnswers] = useRemoteDataMutation<
    GQL.DeleteAnswers,
    GQL.DeleteAnswersVariables
  >(DELETE_ANSWERS);

  const isUpserting = upsertFormAnswersStatus.remoteData.isLoading();

  const canEditQuestion = React.useCallback(
    (question: AF.Question<AF.WithId>) => {
      // If there is a CQT in an unlinked form (guardianId is undefined)
      // disable the question as the answer bank functionality requires a guardian.
      if (question.type === AF.CustomQuestionType && !guardianId) {
        return false;
      }
      if (authorizationMap.hasData()) {
        return hasPermission({
          action: {
            type: "Form::Action",
            id: "edit",
          },
          resource: {
            type: "FormQuestion",
            id: question.key || question.id,
          },
          authorizationMap: authorizationMap.data,
        });
      }
      return false;
    },
    [authorizationMap, guardianId]
  );

  const initialAnswers = Answer.getFormikInitialValues(
    questions,
    form_answers,
    grades_answers,
    address_answers,
    custom_question_answers,
    custom_question_answer_bank_relationships
  );
  const rankedSchoolIds = rankedSchools.map(
    (rankedSchool) => rankedSchool.school.id
  );
  const toast = useAvelaToast();
  const visibilityContext = useVisibilityContext();
  const removeAnswersForHiddenQuestions = useRemoveAnswersForHiddenQuestions({
    formId,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitHandler = async (answers: Answer.FormikValues) => {
    try {
      setIsSubmitting(true);
      const completeQuestions = Question.getCompleteQuestions(questions);
      const applicableQuestions = Question.getCompleteApplicableQuestions(
        questions,
        answers,
        { rankedSchoolIds, previousFormSchoolIds },
        visibilityContext
      );
      const modifiedQuestions = applicableQuestions.filter(
        (q) => !isEqual(answers[q.id], initialAnswers[q.id])
      );

      const freeTextAnswers = getFreeTextAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const singleSelectAnswers = getSingleSelectAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const multiSelectAnswers = getMultiSelectAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const singleOrMultiSelectAnswers =
        singleSelectAnswers.concat(multiSelectAnswers);
      const gradesAnswers = getGradesAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const addressAnswers = getAddressAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const addressAnswersDeletes = getAddressAnswersDeletes(
        formId,
        modifiedQuestions,
        answers
      );
      const numberAnswers = getNumberAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const dateAnswers = getDateAnswersInserts(
        formId,
        modifiedQuestions,
        answers
      );
      const {
        customQuestionAnswers,
        answerBankMappingIds,
        answerBankAnswers,
        newAnswerBankEntries,
        customQuestionAnswersToDelete,
        customQuestionAnswerFieldsToDelete,
        externalRelationships,
        externalRelationshipsToDelete,
      } = getCustomQuestionAnswerUpsertPayload(
        formId,
        guardianId,
        modifiedQuestions,
        answers,
        customQuestionTypes,
        custom_question_answer_bank_relationships
      );

      const customQuestions = modifiedQuestions.filter(
        (q) => q.type === AF.CustomQuestionType
      );

      const customQuestionIds = customQuestions.map((cq) => cq.id);

      const customQuestionSingleSelectQuestionIds: uuid[] =
        customQuestions.flatMap((q) =>
          (q as AF.CustomQuestion<AF.WithId>).nestedQuestions
            .filter((nq) => nq.type === AF.SingleSelectType)
            .map((nq) => nq.id)
        );
      // Existing single and multi select question answers,
      // including those that are nested in custom questions,
      // will be deleted
      const singleAndMultiSelectQuestionsIds: uuid[] =
        singleOrMultiSelectAnswers
          .map((a) => a.question_id)
          .filter(isNotNull)
          .concat(customQuestionSingleSelectQuestionIds)
          .concat(customQuestionAnswerFieldsToDelete); // this deliberately overloads "singleAndMultiSelectQuestionsIds" to support "Do not answer"

      const inapplicableQuestions = Question.findInapplicableQuestionsToDelete(
        completeQuestions,
        applicableQuestions,
        answers,
        visibilityContext
      );

      const allNewAnswers = freeTextAnswers
        .concat(singleOrMultiSelectAnswers)
        .concat(numberAnswers)
        .concat(dateAnswers);

      await removeAnswersForHiddenQuestions();
      await Promise.all([
        upsertFormAnswers(
          {
            form_id: formId,
            answers: allNewAnswers,
            single_or_multi_select_question_ids:
              singleAndMultiSelectQuestionsIds,
            grades_answers: gradesAnswers,
            address_answers: addressAnswers,
            address_answer_deletes: addressAnswersDeletes,
            custom_question_ids: customQuestionIds,
            custom_question_answers: customQuestionAnswers,
            answer_bank_ids: answerBankMappingIds.map((ab) => ab.answerBankId),
            answer_bank_answers: answerBankAnswers,
            new_answer_bank_entries: newAnswerBankEntries,
            deleted_answers_custom_question_ids: customQuestionAnswersToDelete,
          },
          {
            answerBankMappingIds,
            externalRelationships,
            externalRelationshipsToDelete,
          },
          {
            refetchQueries: [
              GET_FORM_VIEW_BY_ID,
              GET_FORM_ANSWERS_BY_ID,
              GET_SCHOOLS_RANK_VIEW,
              // Refetch CQT queries to update outdated answer alerts
              // These queries are used by OutdatedAnswerAlert to compare form answers with answer bank
              GET_CUSTOM_QUESTION_ANSWER,
              GET_SELECTED_PERSON_ANSWER_BANK,
            ],
            awaitRefetchQueries: true,
          }
        ),
        inapplicableQuestions.length > 0
          ? deleteAnswers({
              variables: {
                form_id: formId,
                question_ids: inapplicableQuestions,
              },
            })
          : _.noop(),
      ]);

      await onRefetchFormAlerts?.();
      onCloseEdit();
    } catch (error: unknown) {
      console.error(`[FormSubmit] Form submission failed:`, error);
      toast.error({
        title: "Error updating form",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkAdminPermission = (question: AF.Question<AF.WithId>) =>
    question.permissionLevel === GQL.person_type_enum.admin &&
    !hasAdminOrgAdminPermission
      ? false
      : true;

  const formAnswersContext = useFormContext();

  return (
    <Formik<Answer.FormikValues>
      initialValues={initialAnswers}
      onSubmit={submitHandler}
      validate={validateFormTemplate(
        questions,
        {
          rankedSchoolIds,
          previousFormSchoolIds,
        },
        visibilityContext
      )}
    >
      {(formikProps) => {
        const completeQuestions = Question.getCompleteApplicableQuestions(
          questions,
          formikProps.values,
          { rankedSchoolIds, previousFormSchoolIds },
          visibilityContext
        );
        const invalidAddressCoordinates = hasInvalidAddressCoordinates(
          completeQuestions,
          formikProps.values
        );

        return (
          <Flex
            direction="column"
            as={FormWithAnswersTracker}
            noValidate
            gap={3}
          >
            <Box>
              {completeQuestions.map(
                (question) =>
                  checkAdminPermission(question) && (
                    <Flex key={question.id} direction="row" gap="2">
                      <FormQuestion
                        formId={formId}
                        applicant={applicant}
                        question={question}
                        marginBottom={4}
                        disableAddressBook
                        disableDocumentLibray
                        isDisabled={
                          isSubmitting ||
                          isUpserting ||
                          !canEditQuestion(question)
                        }
                        disabledReason={
                          question.type === AF.CustomQuestionType && !guardianId
                            ? CQT_NO_GUARDIAN_DISABLED_REASON
                            : undefined
                        }
                        labelPostElement={
                          <Undo
                            formId={formId}
                            question={question}
                            answer={formikProps.values[question.id]}
                            setAnswer={(value) =>
                              formikProps.setFieldValue(question.id, value)
                            }
                          />
                        }
                      />
                    </Flex>
                  )
              )}
            </Box>
            <Flex justifyContent="flex-end" gap={3}>
              <Button
                type="button"
                variant="ghost"
                colorScheme="gray"
                onClick={() => {
                  formAnswersContext.updateAnswers(formikProps.initialValues);
                  onCloseEdit();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={!formikProps.isValid || invalidAddressCoordinates}
                isLoading={isSubmitting || isUpserting}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        );
      }}
    </Formik>
  );
};
